<script setup>
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useAlert } from "dashboard/composables";
import DealsAPI from "dashboard/api/crm/deals";
import ProductsAPI from "dashboard/api/crm/products";

import Dialog from "dashboard/components-next/dialog/Dialog.vue";
import CrmMultiSelectSearch from "dashboard/components/crm/CrmMultiSelectSearch.vue";

const props = defineProps({
  deal: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["updated"]);

const { t } = useI18n();
const dialogRef = ref(null);

const productCatalog = ref([]);
const isSaving = ref(false);
const selectedProducts = ref([]);

// Moneda "bloqueada" por lo ya seleccionado: en cuanto hay un producto elegido,
// solo se puede seguir agregando de la misma moneda — igual que exige el backend
// (Crm::Deal#products_must_have_same_currency). Sin selección, no hay restricción.
const selectedCurrency = computed(() => {
  if (!selectedProducts.value.length) return null;
  const selectedIds = new Set(selectedProducts.value.map((item) => item.id));
  const match = productCatalog.value.find((p) => selectedIds.has(p.id));
  return match?.currency || null;
});

const productOptions = computed(() => {
  const catalog = selectedCurrency.value
    ? productCatalog.value.filter((p) => p.currency === selectedCurrency.value)
    : productCatalog.value;
  return catalog.map((p) => ({
    id: p.id,
    name: p.name + (p.formatted_price ? ` - ${p.formatted_price}` : ""),
  }));
});

// Catálogo cargado perezosamente: solo al abrir el modal, nunca al montar el panel.
const fetchCatalog = async () => {
  try {
    const { data } = await ProductsAPI.get({ active: "true" });
    productCatalog.value = Array.isArray(data) ? data : data?.products || [];
  } catch {
    productCatalog.value = [];
  }
};

const open = () => {
  selectedProducts.value = props.deal?.products || [];
  if (!productCatalog.value.length) fetchCatalog();
  dialogRef.value?.open();
};

const save = async () => {
  if (!props.deal) return;
  isSaving.value = true;
  try {
    const productIds = selectedProducts.value.map((item) => item.id);
    const { data } = await DealsAPI.update(props.deal.id, {
      product_ids: productIds,
    });
    emit("updated", data);
    dialogRef.value?.close();
  } catch (error) {
    useAlert(error.message || t("CONTACT_PANEL.MERCADOPAGO.CART_UPDATE_ERROR"));
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('CONTACT_PANEL.MERCADOPAGO.EDIT_PRODUCTS_TITLE')"
    :is-loading="isSaving"
    :confirm-button-label="t('CONTACT_PANEL.MERCADOPAGO.SAVE_PRODUCTS')"
    @confirm="save"
  >
    <div class="flex flex-col gap-1">
      <CrmMultiSelectSearch
        v-model="selectedProducts"
        :options="productOptions"
        :placeholder="t('CONTACT_PANEL.MERCADOPAGO.ADD_PRODUCTS')"
        :input-placeholder="t('CONTACT_PANEL.MERCADOPAGO.ADD_PRODUCTS')"
        :no-search-result="t('CONTACT_PANEL.MERCADOPAGO.NO_PRODUCTS')"
      />
      <p v-if="selectedCurrency" class="text-xs text-n-slate-11">
        {{
          t("CONTACT_PANEL.MERCADOPAGO.CURRENCY_LOCKED_HINT", {
            currency: selectedCurrency,
          })
        }}
      </p>
    </div>
  </Dialog>
</template>
